import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { t } from "@/lib/i18n";
import { toast } from "sonner";
import { RefreshCw, ExternalLink, ShoppingBag, Link2, Unlink } from "lucide-react";

const fmtTs = (ts) => (ts ? new Date(ts).toLocaleString() : "—");

export default function ShopifyPage() {
  const { lang } = useLang();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, p] = await Promise.all([
        api.get("/shopify/status"),
        api.get(`/shopify/products?limit=200${q ? `&q=${encodeURIComponent(q)}` : ""}`),
      ]);
      setStatus(s.data);
      setProducts(p.data.products || []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, [q]);

  useEffect(() => {
    load();
    if (searchParams.get("connected") === "1") {
      toast.success(t(lang, "shopify_connected_toast"));
      searchParams.delete("connected");
      setSearchParams(searchParams, { replace: true });
    }
  }, [load, lang, searchParams, setSearchParams]);

  const connect = async () => {
    const shop = status?.domain || "abnc7u-4t.myshopify.com";
    try {
      const { data } = await api.get(`/shopify/oauth/authorize_url?shop=${encodeURIComponent(shop)}`);
      const url = data.authorize_url;

      // The Emergent preview/production wraps our app inside an iframe on app.emergent.sh.
      // Shopify sends X-Frame-Options: DENY on its OAuth pages, so any in-frame navigation
      // is refused by the browser ("recusou estabelecer ligação"). The only reliable way
      // to complete OAuth is to open the authorize URL in a brand new top-level tab.
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        // Popup blocker fired — fall back to plain nav (works when app is NOT iframed).
        toast.info(t(lang, "shopify_new_tab_opened"));
        window.location.href = url;
      } else {
        toast.info(t(lang, "shopify_new_tab_opened"));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Shopify install");
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await api.post("/shopify/disconnect");
      toast.success(t(lang, "shopify_disconnected_toast"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const doSync = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/shopify/sync");
      if (data.ok) toast.success(`${data.fetched} ${t(lang, "shopify_synced")}`);
      else toast.error(data.error || t(lang, "shopify_sync_failed"));
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t(lang, "shopify_sync_failed"));
    } finally {
      setBusy(false);
    }
  };

  const configured = status?.configured;
  const connected = status?.connected;
  const hasError = !!status?.error;

  return (
    <div className="space-y-6" data-testid="shopify-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-light text-3xl tracking-tight">
            {t(lang, "shopify_title")}
          </h1>
          <p className="mt-1 text-white/50 text-sm">{t(lang, "shopify_subtitle")}</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="shopify-refresh"
            onClick={load}
            disabled={loading}
            className="hq-btn hq-btn-ghost"
            aria-label="Refresh"
          >
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
          </button>
          {connected ? (
            <>
              <button
                data-testid="shopify-register-webhook-btn"
                onClick={async () => {
                  try {
                    const { data } = await api.post("/shopify/webhooks/register");
                    if (data.ok) toast.success(data.already_registered ? "Already registered" : t(lang, "intel_webhook_ok"));
                    else toast.error(data.error || "Failed");
                  } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
                }}
                className="hq-btn hq-btn-ghost"
              >
                {t(lang, "intel_register_webhook")}
              </button>
              <button
                data-testid="shopify-disconnect-btn"
                onClick={disconnect}
                disabled={busy}
                className="hq-btn hq-btn-ghost disabled:opacity-40"
              >
                <Unlink size={12} /> {t(lang, "shopify_disconnect")}
              </button>
              <button
                data-testid="shopify-sync-btn"
                onClick={doSync}
                disabled={busy}
                className="hq-btn hq-btn-primary disabled:opacity-40"
              >
                {busy ? t(lang, "shopify_syncing") : t(lang, "shopify_sync_now")}
              </button>
            </>
          ) : (
            <button
              data-testid="shopify-connect-btn"
              onClick={connect}
              disabled={!configured}
              className="hq-btn hq-btn-primary disabled:opacity-40"
            >
              <Link2 size={12} /> {t(lang, "shopify_connect")}
            </button>
          )}
        </div>
      </div>

      {/* Status card */}
      <div className="hq-card p-6">
        <div className="grid gap-4 md:grid-cols-4">
          <StatusRow
            testid="shopify-status-row"
            label={t(lang, "shopify_status")}
            value={
              !configured ? t(lang, "shopify_not_configured")
              : connected && !hasError ? t(lang, "shopify_connected")
              : connected && hasError ? t(lang, "shopify_error")
              : t(lang, "shopify_disconnected")
            }
            tone={connected && !hasError ? "green" : hasError ? "red" : "yellow"}
          />
          <StatusRow testid="shopify-shop-row" label={t(lang, "shopify_shop")} value={status?.shop_name || status?.domain || "—"} />
          <StatusRow testid="shopify-currency-row" label={t(lang, "shopify_currency")} value={status?.currency || "—"} />
          <StatusRow testid="shopify-lastsync-row" label={t(lang, "shopify_last_sync")} value={fmtTs(status?.last_sync_at)} />
        </div>
        {hasError && (
          <div className="mt-4 border border-[color:var(--hq-swiss-red)]/40 bg-[color:var(--hq-swiss-red)]/5 p-3 rounded font-mono text-xs text-[color:var(--hq-swiss-red)]" data-testid="shopify-error-message">
            {status.error}
          </div>
        )}
        {!connected && configured && (
          <div className="mt-4 border border-white/10 bg-white/[0.02] p-4 rounded text-sm text-white/70" data-testid="shopify-connect-hint">
            <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
              {t(lang, "shopify_connect_hint_title")}
            </div>
            {t(lang, "shopify_connect_hint_body")}
          </div>
        )}
      </div>

      {/* Products */}
      <div className="hq-card overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <ShoppingBag size={14} className="text-[color:var(--hq-cyan)]" />
            <div className="font-display text-lg">{t(lang, "shopify_products")}</div>
            <span className="font-mono text-xs text-white/40">
              {products.length} / {status?.products_count ?? 0}
            </span>
          </div>
          <input
            data-testid="shopify-search"
            className="hq-input max-w-xs"
            placeholder={t(lang, "shopify_search_placeholder")}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>

        {products.length === 0 ? (
          <div className="text-center text-white/40 py-16 font-mono text-xs">
            {connected ? t(lang, "shopify_empty") : t(lang, "shopify_not_connected")}
          </div>
        ) : (
          <div className="p-5 grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {products.map((p) => (
              <ProductCard key={p.shopify_id} p={p} lang={lang} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


function ProductCard({ p, lang }) {
  const chf = (n) =>
    n == null ? "—" : new Intl.NumberFormat("de-CH", { style: "currency", currency: "CHF" }).format(n);
  const price = p.price_chf;

  const statusColor = {
    active: "text-[color:var(--hq-success)] border-[color:var(--hq-success)]/30 bg-[color:var(--hq-success)]/5",
    draft: "text-white/60 border-white/15 bg-white/[0.02]",
    archived: "text-white/40 border-white/10 bg-white/[0.01]",
  }[p.status || "draft"] || "text-white/60 border-white/15 bg-white/[0.02]";

  return (
    <div
      data-testid={`shopify-row-${p.shopify_id}`}
      className="group border border-white/10 rounded-lg overflow-hidden bg-white/[0.02] hover:border-[color:var(--hq-cyan)]/40 transition-colors duration-300"
    >
      {/* Image */}
      <div className="aspect-square bg-[#0a0a0a] relative overflow-hidden">
        {p.image ? (
          <img
            src={p.image}
            alt={p.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <ShoppingBag size={32} className="text-white/15" />
          </div>
        )}
        <div className={`absolute top-2 left-2 font-mono text-[9px] uppercase tracking-widest border px-2 py-0.5 rounded-full ${statusColor}`}>
          {p.status || "draft"}
        </div>
        {p.url && (
          <a
            href={p.url}
            target="_blank"
            rel="noreferrer"
            className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 backdrop-blur border border-white/10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200"
            data-testid={`shopify-open-${p.shopify_id}`}
            aria-label="Open in Shopify"
          >
            <ExternalLink size={12} className="text-white/80" />
          </a>
        )}
      </div>

      {/* Body */}
      <div className="p-4">
        <div
          className="text-white font-medium text-sm leading-snug min-h-[2.5rem] line-clamp-2"
          title={p.title}
        >
          {p.title}
        </div>
        <div className="mt-3 flex items-baseline justify-between gap-2">
          <div className="font-display text-xl text-white">
            {chf(price)}
          </div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">
            {p.variants_count || 1} {p.variants_count === 1 ? "variant" : "variants"}
          </div>
        </div>
        <div className="mt-2 flex items-center gap-2 flex-wrap text-[10px] text-white/50">
          {p.vendor && (
            <span className="font-mono uppercase tracking-widest">{p.vendor}</span>
          )}
          {p.product_type && (
            <>
              <span className="text-white/25">·</span>
              <span className="font-mono uppercase tracking-widest">{p.product_type}</span>
            </>
          )}
        </div>
        <div className="mt-2 font-mono text-[10px] text-white/30">
          {t(lang, "shopify_col_updated")}: {fmtTs(p.updated_at)}
        </div>
      </div>
    </div>
  );
}

function StatusRow({ label, value, tone = "default", testid }) {
  const cls =
    tone === "green" ? "text-[color:var(--hq-success)]"
    : tone === "red" ? "text-[color:var(--hq-swiss-red)]"
    : tone === "yellow" ? "text-[color:var(--hq-warning)]"
    : "text-white";
  return (
    <div data-testid={testid}>
      <div className="font-mono text-[10px] uppercase tracking-widest text-white/40 mb-1">
        {label}
      </div>
      <div className={`font-mono text-sm ${cls}`}>{value}</div>
    </div>
  );
}
